import { isSemanticSearchCalledAtom } from '@components/listings-search-bar/atoms';
import {
	appliedFacetIdsAtom,
	selectedCategoryCodesAtom,
	shouldNoIndexForCategoryAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import * as ExperimentActions from '@redux/experiments';
import { compassModelLocationSelector } from '@redux/experiments/selectors/compass-model-location';
import { directoryPageFiltersSelector } from '@redux/experiments/selectors/directory-page-filters';
import { improveMpPerformanceSelector } from '@redux/experiments/selectors/improve-mp-performance';
import { improveMpPerformanceTier2Selector } from '@redux/experiments/selectors/improve-mp-performance-tier-2';
import { improveVendorCardScannabilitySelector } from '@redux/experiments/selectors/improve-vendor-card-scannability';
import { mobileMapViewV2AssignmentSelector } from '@redux/experiments/selectors/mobile-map-view-v2';
import { novaV1Selector } from '@redux/experiments/selectors/nova-v1';
import { recentlyViewedAssignmentSelector } from '@redux/experiments/selectors/recently-viewed';
import { requestQuoteCtaChangeSelector } from '@redux/experiments/selectors/request-quote-cta-change';
import { sortRelevanceModelSelector } from '@redux/experiments/selectors/sort-relevance-model';
import { storefrontCompletionPenaltySelector } from '@redux/experiments/selectors/storefront-completion-penalty';
import { typeaheadTypoToleranceSelector } from '@redux/experiments/selectors/typeahead-typo-tolerance';
import { vendorCardUiUpdatesSelector } from '@redux/experiments/selectors/vendor-card-ui-updates';
import { venueCardAttributesSelector } from '@redux/experiments/selectors/venue-card-attributes';
import { useAppSelector } from '@redux/hooks';
import { experiments, tkHost } from '@settings';
import { Logger } from '@utils/logger/logger';
import { useAtomValue } from 'jotai';
import React, {
	type FunctionComponent,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { connect } from 'react-redux';
import { type RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import buildLinkedDataFromApiData from '../../../helpers/linkedData';
import {
	buildLocalBusinessItemListScript,
	getDirectoryLeadImage,
} from '../../../helpers/linkedData/localBusiness';
import MetaContent from '../../components/shared/HelmetHeader/HelmetHeader';
import { RFQModalContextProvider } from '../../contexts/RFQModalContext/RFQModalContext';
import { WeddingsContextProvider } from '../../contexts/WeddingsContext/WeddingsContext';
import {
	metaDescriptionAtom,
	metaTitleAtom,
} from '../../jotai/shared/seo-experiment';
import { setCookieLocation } from '../../utils/cookie/location';
import { getDisplayedDirectoryVendors } from './containers/SearchResults/components/ListingsSectionsGrid/selectors';
import WrappedVendorsSearch from './containers/WrappedVendorsSearch';
import { useOrderRecentlySeenDirectories } from './hooks/useOrderRecentlySeenDirectories';
import * as util from './util';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

type Props = StateProps & DispatchProps & RouteComponentProps;

const VendorsSearchContainer: FunctionComponent<Props> = (props) => {
	const {
		category,
		vendorLocation,
		history: {
			location: { pathname },
		},
		isPermitted,
		isSearchFetching,
		searchPage,
		searchVendors,
		reportServerSideExperiment,
		seo,
	} = props;

	useOrderRecentlySeenDirectories();
	const isSemanticSearchCalled = useAtomValue(isSemanticSearchCalledAtom);
	const appliedFacetIds = useAtomValue(appliedFacetIdsAtom);
	const selectedCategoryCodes = useAtomValue(selectedCategoryCodesAtom);
	const shouldNoIndexForCategory = useAtomValue(shouldNoIndexForCategoryAtom);
	const seoCategory = useMemo(() => {
		return util.categoryText(category, appliedFacetIds);
	}, [category, appliedFacetIds]);

	const metaTitleFromExperiment = useAtomValue(metaTitleAtom);
	const metaDescriptionFromExperiment = useAtomValue(metaDescriptionAtom);
	const metaTitle =
		metaTitleFromExperiment === null
			? util.pageTitle(vendorLocation, seoCategory, category.code)
			: metaTitleFromExperiment;
	const defaultMetaDescription = `Learn more about ${seoCategory.toLowerCase()} in ${vendorLocation.city} on The Knot.
Find, research and contact wedding professionals on The Knot, featuring reviews
and info on the best wedding vendors.`;
	const metaDescription =
		metaDescriptionFromExperiment === null
			? defaultMetaDescription
			: metaDescriptionFromExperiment;

	const recentlyViewedAssignment = useAppSelector((state) =>
		recentlyViewedAssignmentSelector(state),
	);

	const mobileMapViewV2Assignment = useAppSelector((state) =>
		mobileMapViewV2AssignmentSelector(state),
	);

	const vendorCardUiUpdatesAssignment = useAppSelector((state) =>
		vendorCardUiUpdatesSelector(state),
	);

	const compassModelLocationAssignment = useAppSelector((state) =>
		compassModelLocationSelector(state),
	);

	const storefrontCompletionPenaltyAssignment = useAppSelector((state) =>
		storefrontCompletionPenaltySelector(state),
	);

	const directoryPageFiltersAssignment = useAppSelector((state) =>
		directoryPageFiltersSelector(state),
	);

	const hasReportedDirectoryPageFiltersRef = useRef(false);
	const requestQuoteCtaChangeAssignment = useAppSelector(
		requestQuoteCtaChangeSelector,
	);
	const hasReportedRequestQuoteCtaChangeRef = useRef(false);

	const improveMpPerformanceAssignment = useAppSelector((state) =>
		improveMpPerformanceSelector(state),
	);
	const improveMpPerformanceTier2Assignment = useAppSelector((state) =>
		improveMpPerformanceTier2Selector(state),
	);
	const sortRelevanceModelAssignment = useAppSelector((state) =>
		sortRelevanceModelSelector(state),
	);

	const novaV1Assignment = useAppSelector((state) => novaV1Selector(state));

	const venueCardAttributesAssignment = useAppSelector((state) =>
		venueCardAttributesSelector(state),
	);

	const improveVendorCardScannabilityAssignment = useAppSelector(
		improveVendorCardScannabilitySelector,
	);

	const linkedDataScripts = useMemo(() => {
		const displayedVendors = getDisplayedDirectoryVendors(searchVendors);
		if (isSearchFetching || searchPage !== 1 || displayedVendors.length === 0) {
			return null;
		}

		return buildLocalBusinessItemListScript({
			id: 'directoryLocalBusinessLdJson',
			vendors: displayedVendors,
			imageForVendor: getDirectoryLeadImage,
		});
	}, [isSearchFetching, searchPage, searchVendors]);

	const typeaheadTypoToleranceAssignment = useAppSelector((state) =>
		typeaheadTypoToleranceSelector(state),
	);

	useEffect(() => {
		window.UnionConsentManagement.onConsentedToPersonalisation(() => {
			setCookieLocation(vendorLocation);
		});

		if (recentlyViewedAssignment) {
			// biome-ignore lint/suspicious/noConsoleLog: Experiment logging
			console.log('Recently viewed assignment: ', recentlyViewedAssignment);
			reportServerSideExperiment(experiments.recentlyViewed);
		}

		if (mobileMapViewV2Assignment) {
			// biome-ignore lint/suspicious/noConsoleLog: Experiment logging
			console.log('Mobile Map View V2 Assignment: ', mobileMapViewV2Assignment);
			reportServerSideExperiment(experiments.mobileMapViewV2);
		}

		if (vendorCardUiUpdatesAssignment) {
			Logger.log({
				log_message: `Vendor Card UI Updates Assignment: ${vendorCardUiUpdatesAssignment}`,
			});
			reportServerSideExperiment(experiments.vendorCardUiUpdates);
		}

		if (compassModelLocationAssignment) {
			Logger.log({
				log_message: `Compass Model Location Assignment: ${compassModelLocationAssignment}`,
			});
			reportServerSideExperiment(experiments.compassModelLocation);
		}

		if (storefrontCompletionPenaltyAssignment) {
			Logger.log({
				log_message: `SSS Storefront Completion Penalty Assignment: ${storefrontCompletionPenaltyAssignment}`,
			});
			reportServerSideExperiment(experiments.storefrontCompletionPenalty);
		}

		if (directoryPageFiltersAssignment) {
			if (!hasReportedDirectoryPageFiltersRef.current) {
				Logger.log({
					log_message: `PoM Directory Page Filters Assignment: ${directoryPageFiltersAssignment}`,
				});
				reportServerSideExperiment(experiments.pomDirectoryPageFilters);
				hasReportedDirectoryPageFiltersRef.current = true;
			}
		}

		if (
			requestQuoteCtaChangeAssignment &&
			!hasReportedRequestQuoteCtaChangeRef.current
		) {
			Logger.log({
				log_message: `Request Quote CTA Change Assignment: ${requestQuoteCtaChangeAssignment}`,
			});
			reportServerSideExperiment(experiments.requestQuoteCtaChange);
			hasReportedRequestQuoteCtaChangeRef.current = true;
		}

		if (improveMpPerformanceAssignment) {
			Logger.log({
				log_message: `Improve MP Performance Assignment: ${improveMpPerformanceAssignment}`,
			});
			reportServerSideExperiment(experiments.improveMpPerformance);
		}

		if (improveMpPerformanceTier2Assignment) {
			Logger.log({
				log_message: `Improve MP Performance Tier 2 Assignment: ${improveMpPerformanceTier2Assignment}`,
			});
			reportServerSideExperiment(experiments.improveMpPerformanceTier2);
		}

		if (sortRelevanceModelAssignment) {
			Logger.log({
				log_message: `Sort Relevance Model Assignment: ${sortRelevanceModelAssignment}`,
			});
			reportServerSideExperiment(experiments.sortRelevanceModel);
		}

		if (novaV1Assignment) {
			Logger.log({
				log_message: `Nova v1 Assignment: ${novaV1Assignment}`,
			});
			reportServerSideExperiment(experiments.novaV1);
		}

		if (venueCardAttributesAssignment) {
			Logger.log({
				log_message: `Venue Card Attributes Assignment: ${venueCardAttributesAssignment}`,
			});
			reportServerSideExperiment(experiments.venueCardAttributes);
		}

		if (typeaheadTypoToleranceAssignment) {
			Logger.log({
				log_message: `Typeahead Typo Tolerance Assignment: ${typeaheadTypoToleranceAssignment}`,
			});
			reportServerSideExperiment(experiments.typeaheadTypoTolerance);
		}

		if (improveVendorCardScannabilityAssignment) {
			Logger.log({
				log_message: `Improve Vendor Card Scannability Assignment: ${improveVendorCardScannabilityAssignment}`,
			});

			reportServerSideExperiment(experiments.improveVendorCardScannability);
		}
	}, [
		reportServerSideExperiment,
		vendorLocation,
		recentlyViewedAssignment,
		mobileMapViewV2Assignment,
		vendorCardUiUpdatesAssignment,
		compassModelLocationAssignment,
		storefrontCompletionPenaltyAssignment,
		directoryPageFiltersAssignment,
		requestQuoteCtaChangeAssignment,
		improveMpPerformanceAssignment,
		improveMpPerformanceTier2Assignment,
		sortRelevanceModelAssignment,
		novaV1Assignment,
		venueCardAttributesAssignment,
		typeaheadTypoToleranceAssignment,
		improveVendorCardScannabilityAssignment,
	]);

	return (
		<>
			<WeddingsContextProvider>
				<RFQModalContextProvider>
					<MetaContent
						buildLinkedData={buildLinkedDataFromApiData}
						canonical={seo.canonicalUrl}
						description={metaDescription}
						landingPage="listings"
						{...(linkedDataScripts && { linkedDataScripts })}
						next={seo.nextUrl}
						ogImage={util.ogImage(category.code)}
						pathname={`${tkHost}${pathname}`}
						prev={seo.previousUrl}
						title={metaTitle}
						noIndex={
							!isPermitted || isSemanticSearchCalled || shouldNoIndexForCategory
						}
					/>
					<WrappedVendorsSearch />
				</RFQModalContextProvider>
			</WeddingsContextProvider>
		</>
	);
};

export const mapStateToProps = (state: Redux.State) => ({
	category: state.category,
	isPermitted: state.search.isPermitted,
	isSearchFetching: state.search.isFetching,
	searchPage: state.search.pagination.page,
	searchVendors: state.search.vendors,
	seo: state.seo,
	vendorLocation: state.location,
	vendorCardUiUpdatesAssignment: vendorCardUiUpdatesSelector(state),
});

export const mapDispatchToProps = {
	reportServerSideExperiment: ExperimentActions.reportServerSideExperiment,
};

const enhance = compose(
	connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps),
	withRouter,
);

export default enhance(VendorsSearchContainer);
